import { ChevronDown, LogOut, Menu, PanelLeft } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { AuthModeBadge, SsoOneCInfo } from "@/components/AuthModeIndicator";
import { BuildVersion } from "@/components/BuildVersion";
import { UpdateBanner } from "@/components/UpdateBanner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { useNewVersion } from "@/lib/use-new-version";
import { cn } from "@/lib/utils";
import {
  ACCOUNT_ITEMS,
  ADMIN_ITEMS,
  ONEC_BASES_ADMIN_ITEM,
  SETTINGS_HUB,
  settingsNavItems,
  visibleModuleGroups,
  type NavItem,
} from "@/nav";
import { usePortalConfig } from "@/portal/usePortalConfig";

/** Роуты «в один вьюпорт под ТСД» (сканирование заказа): без внешней шапки/меню
 * и отступов — страница сама занимает весь вьюпорт (`.h-app`: 100dvh минус
 * плашка новой версии). */
function isImmersiveRoute(pathname: string): boolean {
  return (
    /^\/ozon\/cabinets\/[^/]+\/pick/.test(pathname) ||
    pathname.startsWith("/order-check/orders/") ||
    pathname.startsWith("/transfer-check/transfers/") ||
    // Карточка приёмки перемещения — скан привезённого в один вьюпорт.
    pathname.startsWith("/transfer-receipts/") ||
    pathname.startsWith("/returns/sessions/") ||
    // Скан-экран ФБС: сбор в комнате идёт в один вьюпорт без шапки.
    // Завершение поставки (`/fbs/supplies/{id}/finish`) — обычный экран.
    /^\/fbs\/cabinets\/[^/]+\/pick/.test(pathname) ||
    // Проверка отгрузки — такой же скан-цикл в один вьюпорт.
    pathname === "/shipment" ||
    // Приёмка поставок: заказ (приходы по накладным) и сам приход — ТСД-вьюпорт.
    pathname.startsWith("/receiving/orders/") ||
    pathname.startsWith("/receiving/deliveries/") ||
    // Пересчёт склада — такой же скан-цикл в один вьюпорт.
    pathname.startsWith("/inventory/sessions/") ||
    // Перемещение без заказа — скан-цикл в один вьюпорт.
    pathname.startsWith("/stock-transfer/sessions/") ||
    // Грузоместа: скан-цикл агрегации и разагрегации.
    pathname === "/boxes/scan" ||
    // «Что это?» — скан этикетки и справка в один вьюпорт.
    pathname === "/label-info"
  );
}

/** Пункт бокового меню; `nested` — подпункт секции внутри группы «Настройки». */
interface NavEntry {
  item: NavItem;
  nested?: boolean;
}

interface NavGroup {
  title: string;
  entries: NavEntry[];
}

export function Layout(): React.ReactElement {
  const { user, logout } = useAuth();
  const { title, hasPage, settings_sections, sso_enabled: ssoEnabled } = usePortalConfig();
  const navigate = useNavigate();
  const location = useLocation();
  // Опрос версии — здесь, а не в плашке: Layout живёт через все роуты, и флаг
  // «вышла новая версия» не теряется при переходе скан-экран ↔ обычный экран.
  const outdated = useNewVersion();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  // Свёрнутость бокового меню на десктопе — удобство экрана, не данные: сбой
  // localStorage (приватный режим, квота) просто не запоминает выбор.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("navCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = (): void =>
    setCollapsed((s) => {
      try {
        localStorage.setItem("navCollapsed", s ? "0" : "1");
      } catch {
        // приватный режим/квота — сворачивание всё равно работает
      }
      return !s;
    });

  // Меню аккаунта закрывается кликом мимо и Escape — оно перекрывает контент
  // шапки, а на телефоне промах мимо пункта иначе оставляет его висеть.
  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (!accountRef.current?.contains(e.target as Node)) setAccountOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setAccountOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountOpen]);

  // Иммерсивные экраны (скан заказа под ТСД) рендерим без обвязки Layout —
  // страница self-contained на весь вьюпорт, каждый пиксель под скан-цикл.
  // Плашка новой версии — над ней В ПОТОКЕ, экран (`.h-app`) ниже на её высоту.
  if (isImmersiveRoute(location.pathname)) {
    return (
      <>
        {outdated && <UpdateBanner />}
        <Outlet />
      </>
    );
  }

  // Выход из SSO-сессии — тот же `/auth/logout`; уход на выход из браузерной
  // сессии Zitadel (`end_session_url`) делает `AuthContext.logout`.
  const handleLogout = async () => {
    setAccountOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  // Модули — блоками из `nav.ts::MODULE_GROUPS`; блок без видимых пунктов
  // выкинут там же, пустого заголовка в меню нет.
  const moduleGroups = visibleModuleGroups((item) => {
    if (!user) return false;
    // Профиль инсталляции — ПЕРВЫМ, до проверок прав: обе они пропускают
    // администратора (`!user.is_admin`), поэтому «не выдать право» не скрывает
    // пункт выключенного модуля. Раздела здесь нет — значит нет и в меню.
    const page = item.page ?? item.permission;
    if (page && !hasPage(page)) return false;
    if (
      item.permission &&
      !user.is_admin &&
      !user.permissions.includes(item.permission)
    )
      return false;
    return true;
  });

  // Настройки и администрирование — только админу целиком, группами: прав на
  // отдельные страницы настроек нет, есть флаг администратора.
  const isAdmin = !!user?.is_admin;
  const groups: NavGroup[] = [
    ...moduleGroups.map((group) => ({
      title: group.title,
      entries: group.items.map((item) => ({ item })),
    })),
    {
      title: "Настройки",
      entries: isAdmin
        ? [
            { item: SETTINGS_HUB },
            // Набор и ПОРЯДОК подпунктов задаёт сервер (реестр секций
            // инсталляции), фронт даёт только маршрут и значок.
            ...settingsNavItems(settings_sections).map((item) => ({
              item,
              nested: true,
            })),
          ]
        : [],
    },
    {
      title: "Администрирование",
      entries: isAdmin
        ? [
            ...ADMIN_ITEMS,
            // Справочник баз 1С нужен только SSO-входу: без SSO в
            // инсталляции его базы никем не используются.
            ...(ssoEnabled ? [ONEC_BASES_ADMIN_ITEM] : []),
          ].map((item) => ({ item }))
        : [],
    },
  ].filter((group) => group.entries.length > 0);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Плашка и шапка прилипают сверху стопкой: шапка — на высоту плашки ниже
          (`--app-banner-h`, 0 без плашки), сайдбар — под обеими. */}
      {outdated && <UpdateBanner className="sticky top-0 z-40" />}
      <header className="sticky top-[var(--app-banner-h)] z-30 flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setDrawerOpen((s) => !s)}
            aria-label="Меню"
          >
            <Menu className="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
          >
            <PanelLeft className="size-5" />
          </Button>
          <span className="text-lg font-semibold">{title}</span>
        </div>
        <div className="relative flex min-w-0 items-center gap-2 text-sm" ref={accountRef}>
          {/* Логин виден ВСЕГДА, и на телефоне тоже: люди должны видеть, под кем
              зашли — иначе документы уходят под чужой 1С-учёткой незаметно.
              Он же — кнопка меню аккаунта (личное подключение 1С, выход). */}
          <button
            type="button"
            onClick={() => setAccountOpen((s) => !s)}
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            aria-label="Меню аккаунта"
            className="flex min-w-0 items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <span className="max-w-[40vw] truncate">{user?.username}</span>
            <AuthModeBadge mode={user?.auth_mode} />
            <ChevronDown className="size-3 shrink-0" />
          </button>
          {accountOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-40 mt-1 w-64 overflow-hidden rounded-md border bg-card py-1 shadow-md"
            >
              {user?.auth_mode === "sso" && <SsoOneCInfo />}
              {ACCOUNT_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <item.icon className="size-4 shrink-0" />
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <LogOut className="size-4 shrink-0" />
                Выйти
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1">
        {/* Высота задана `100dvh`, а не `inset-y-14`: на телефоне нижняя панель
            браузера накрывает layout-viewport, и `bottom: 0` уезжает под неё —
            последние пункты меню оказывались за экраном (боевой отчёт
            20.08.2026: «до них нельзя доскролить вообще никак»). Шапка и
            плашка новой версии (`--app-banner-h`) вычитаются. */}
        <aside
          className={cn(
            "fixed left-0 top-[calc(3.5rem_+_var(--app-banner-h))] z-20 h-[calc(100dvh_-_3.5rem_-_var(--app-banner-h))] w-64 shrink-0",
            "overflow-hidden border-r bg-card transition-[width,transform]",
            "md:static md:h-auto md:translate-x-0",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
            collapsed && "md:w-0 md:border-r-0", // на десктопе сворачиваем в ноль — страница на всю ширину
          )}
        >
          <nav className="flex h-full flex-col p-3">
            {/* ⚠️ `min-h-0` обязателен: flex-элемент по умолчанию не сжимается
                меньше своего содержимого, и `overflow-y-auto` без него не
                включается вовсе — список просто вылезает за пределы панели.
                `overscroll-contain` не даёт прокрутке провалиться на страницу
                под меню. */}
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain">
              {/* Группа — `role="group"` с именем-заголовком: и для скринридера,
                  и чтобы «Настройки» отличались от одноимённых пунктов-модулей
                  («Проверка заказов» есть и там, и там). */}
              {groups.map((group) => (
                <div
                  key={group.title}
                  role="group"
                  aria-label={group.title}
                  className="flex shrink-0 flex-col gap-1"
                >
                  <div className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {group.title}
                  </div>
                  {group.entries.map(({ item, nested }) =>
                    item.error ? (
                      // Сломанная секция (см. `settingsNavItems`): видимая
                      // ошибка, а не ссылка — вести пользователя некуда.
                      <div
                        key={item.to + item.label}
                        className={cn(
                          "flex shrink-0 items-center gap-3 rounded-md py-3 pr-3 text-sm text-destructive",
                          nested ? "pl-8" : "pl-3",
                        )}
                      >
                        <item.icon className="size-4 shrink-0" />
                        {item.label}
                      </div>
                    ) : (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/" || item.to === SETTINGS_HUB.to}
                        onClick={() => setDrawerOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex shrink-0 items-center gap-3 rounded-md py-3 pr-3 text-sm transition-colors",
                            nested ? "pl-8" : "pl-3",
                            isActive
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                          )
                        }
                      >
                        <item.icon className="size-4 shrink-0" />
                        {item.label}
                      </NavLink>
                    ),
                  )}
                </div>
              ))}
            </div>
            {/* Версия остаётся на виду и не уезжает вместе со списком.
                `env(safe-area-inset-bottom)` — под жест-бар iPhone. */}
            <div className="shrink-0 border-t px-1 pt-3 pb-[env(safe-area-inset-bottom)]">
              <BuildVersion />
            </div>
          </nav>
        </aside>

        {drawerOpen && (
          <button
            type="button"
            className="fixed inset-0 z-10 bg-black/30 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-label="Закрыть меню"
          />
        )}

        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
